import { zodResolver } from '@hookform/resolvers/zod'
import MDEditor from '@uiw/react-md-editor'
import { ArrowLeft, Plus, Trash2 } from 'lucide-react'
import React from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { useSupabaseClient } from 'src/api/supabase'
import {
  FormField,
  FormItem,
  FormControl,
} from 'src/shared/components/ui/form'
import { FormWrapper, FormInput } from 'src/shared/components/FormComponents'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/shared/components/ui/table'
import { PageContainer } from 'src/shared/components/PageContainer'
import { paths } from 'src/routes/paths'
import { parseJson } from '../utils'

import { DEFAULT_ROW, formSchema } from './consts'
import type { AddSharedDecorationsFormValues } from './types'

export const AddSharedDecorationsPage = () => {
  const navigate = useNavigate()
  const supabase = useSupabaseClient()

  const methods = useForm<AddSharedDecorationsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { rows: [DEFAULT_ROW] },
  })

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'rows',
  })

  const handleSubmit = async (data: AddSharedDecorationsFormValues) => {
    const inserts = data.rows
      .filter((r) => r.name.trim() || r.one_liner.trim())
      .map((r) => ({
        name: r.name.trim() || null,
        one_liner: r.one_liner.trim() || null,
        subtitle: r.subtitle.trim() || null,
        link: r.link.trim() || null,
        video_url: r.video_url.trim() || null,
        content: r.content.trim() || null,
        image_urls: parseJson(r.image_urls),
      }))
    if (inserts.length === 0) return

    try {
      await (supabase as any).from('decoration_shared_info').insert(inserts)
      window.history.back()
    } catch (error) {
      console.error('Failed to create shared decorations:', error)
    }
  }

  return (
    <PageContainer width="xl">
      <Column className="gap-4">
        <Row className="gap-2 items-center mb-4 py-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(paths.adminDecorationsCosts)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1>Add shared decorations</h1>
        </Row>

        <FormWrapper methods={methods} onSubmit={handleSubmit}>
          <Column className="gap-4">
            <Column className="border rounded-md overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]" />
                    <TableHead>Name</TableHead>
                    <TableHead>One liner</TableHead>
                    <TableHead>Subtitle</TableHead>
                    <TableHead>Link</TableHead>
                    <TableHead>Video URL</TableHead>
                    <TableHead>Image URLs (JSON)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fields.map((field, i) => (
                    <React.Fragment key={field.id}>
                      <TableRow>
                        <TableCell className="w-[40px] p-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => remove(i)}
                            disabled={fields.length <= 1}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                        <TableCell className="p-1">
                          <FormInput
                            name={`rows.${i}.name`}
                            methods={methods}
                            placeholder="Name"
                            className="h-9 border-0 bg-transparent focus-visible:ring-1"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <FormInput
                            name={`rows.${i}.one_liner`}
                            methods={methods}
                            placeholder="One liner"
                            className="h-9 border-0 bg-transparent focus-visible:ring-1"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <FormInput
                            name={`rows.${i}.subtitle`}
                            methods={methods}
                            placeholder="Subtitle"
                            className="h-9 border-0 bg-transparent focus-visible:ring-1"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <FormInput
                            name={`rows.${i}.link`}
                            methods={methods}
                            placeholder="Link"
                            className="h-9 border-0 bg-transparent focus-visible:ring-1"
                          />
                        </TableCell>
                        <TableCell className="p-1">
                          <FormInput
                            name={`rows.${i}.video_url`}
                            methods={methods}
                            placeholder="Video URL"
                            className="h-9 border-0 bg-transparent focus-visible:ring-1"
                          />
                        </TableCell>
                        <TableCell className="p-1 min-w-[140px]">
                          <FormInput
                            name={`rows.${i}.image_urls`}
                            methods={methods}
                            placeholder='["url1", "url2"]'
                            className="h-9 border-0 bg-transparent focus-visible:ring-1 font-mono text-xs"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={7} className="pt-2 pb-4">
                          <FormField
                            control={methods.control}
                            name={`rows.${i}.content`}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Column className="gap-2">
                                    <label className="text-sm font-bold text-gray-500">
                                      Content
                                    </label>
                                    <div data-color-mode="light">
                                      <MDEditor
                                        value={field.value ?? ''}
                                        onChange={(v) =>
                                          field.onChange(v ?? '')
                                        }
                                        height={200}
                                        preview="live"
                                      />
                                    </div>
                                  </Column>
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </Column>

            <Row className="gap-2 justify-between items-center">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => append(DEFAULT_ROW)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add row
              </Button>

              <Row className="gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate(paths.adminDecorationsCosts)}
                >
                  Cancel
                </Button>

                <Button type="submit" variant="primary">
                  Add
                </Button>
              </Row>
            </Row>
          </Column>
        </FormWrapper>
      </Column>
    </PageContainer>
  )
}
